import { useState, useMemo, useCallback, useContext } from 'react';
import { StaticContext } from '@sm/webassets';
import { getDataCenterIdFromCurrentUrl, getDataCenterById } from '@sm/locale';

import { FeatureSet, SummaryDisplayFeatureList } from '~shared/types/pricing/features';
import { Packages } from '~shared/constants/pricing';
import { usePricingExperiments } from '~shared/hooks/usePricingExperiments';

import getSummaryFeatureTransformer from '~pricing/transformers/summary';
import { formatCostInLocaleCurrency } from '~pricing/lib/lib-helpers';

import useFeatureSet from './useFeatureSet';
import { usePricingState } from '~shared/hooks/usePricingState';

/**
 * Given an array of features and the pricing package to be displayed,
 * returns the values for the Feature Summary table.
 * @param {FeatureSet}  features    An array of Feature
 * @param {Packages}  packageName   A value from the Packages enum
 * @returns {SummaryDisplayFeatureList}   An array of DisplayFeatureStatic
 */
export default function useSummaryDisplayFeatures(
  features: FeatureSet,
  packageName: Packages
): SummaryDisplayFeatureList {
  const [displayFeatures, setDisplayFeatures] = useState<SummaryDisplayFeatureList>([]);

  const {
    environment: { slLanguageLocale, countryCode },
    hostname,
    url,
  } = useContext(StaticContext);

  const { uiExperiment, priceExperiment } = usePricingExperiments();
  const {
    state: {
      packages: { overageCost },
    },
  } = usePricingState();
  const experience =
    (uiExperiment.hasAssignment && uiExperiment.treatmentName) ||
    (priceExperiment.hasAssignment && priceExperiment.treatmentName);
  const dataCenter = getDataCenterById(getDataCenterIdFromCurrentUrl(`${hostname}${url}`));

  const featureTransformer = useCallback(
    () => getSummaryFeatureTransformer(packageName, experience as string),
    [packageName, experience]
  );

  const featureSetMap = useFeatureSet(features);

  const formattedOverageCost = useMemo(
    () => overageCost && formatCostInLocaleCurrency(overageCost, 2),
    [overageCost]
  ) as string;

  useMemo(() => {
    if (featureTransformer) {
      const featureSetData = featureTransformer()(featureSetMap, {
        locale: slLanguageLocale,
        countryCode,
        dataCenter: dataCenter?.regionCode,
        packageName,
        overageCost: formattedOverageCost,
      });
      setDisplayFeatures(featureSetData);
    }
  }, [featureSetMap, featureTransformer, slLanguageLocale, countryCode, dataCenter, packageName, formattedOverageCost]);

  return displayFeatures;
}
